import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  Edit,
  ShieldCloseIcon,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useDispatch } from "react-redux";
import { setCurrentTab } from "../../../store/slices/cfcSlice";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";

const ApplicationPreview = ({ application, viewPage = false }) => {
  const dispatch = useDispatch();
  const nav = useNavigate();

  // Add the missing formatDate function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-IN");
    } catch (error) {
      return "-";
    }
  };

  const handleDeclarationEdit = (tabName) => {
    if (viewPage) {
      nav(`/scheme/cfc?tab=${tabName}`);
    } else {
      dispatch(setCurrentTab(tabName));
    }
  };

  const ReviewSection = ({ title, data, tabName }) => {
    const handleEdit = (tabName) => {
      if (viewPage) {
        nav(`/scheme/cfc?tab=${tabName}`);
      } else {
        dispatch(setCurrentTab(tabName));
      }
    };

    return (
      <Card className="mb-4 bg-white p-3">
        <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent style={{ marginTop: "15px" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.map((item, idx) => (
              <div key={idx} className="space-y-1">
                <p className="text-muted-foreground text-sm">{item.label}</p>
                <div className="font-medium">{item.value || "-"}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const FileReviewSection = ({ title, files }) => (
    <Card className="mb-4 bg-white p-3">
      <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {files.map((file, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 border rounded-lg"
            >
              <div className="flex-1">
                <Label className="text-sm font-medium">{file.label}</Label>
                {file.value ? (
                  <div className="flex items-center gap-2 mt-1">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Uploaded</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-600">Not Uploaded</span>
                  </div>
                )}
              </div>
              {file.value && (
                <a
                  href={file.preview || file.value}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline text-sm"
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const beneficiaryDetails = [
    {
      label: "Type of SPV",
      value: application?.beneficiary?.is_spv || "-",
    },
    ...(application?.beneficiary?.is_spv === "Any Other (Specify)"
      ? [
          {
            label: "Specify the type of SPV",
            value: application?.beneficiary?.spv_other_specify || "-",
          },
        ]
      : []),
    {
      label: "EB in the Name of",
      value: application?.beneficiary?.eb_registered_on || "-",
    },
    ...(application?.beneficiary?.eb_registered_on === "Member of the SPV"
      ? [
          {
            label: "Name of the Member of the SPV",
            value: application?.beneficiary?.eb_registered_member_name || "-",
          },
        ]
      : []),
    {
      label:
        "Do the SPV have purchased machinery which is / are included in the machinery list MC-1B-Weaving Preparatory S.No.08 of GOI.",
      value:
        application?.beneficiary?.component_included_in_machinery_list || "-",
    },
    ...(application?.beneficiary?.component_included_in_machinery_list === "yes"
      ? [
          {
            label: "Details of Machinery",
            value: application?.beneficiary?.details_machinery || "-",
          },
          {
            label: "Cost of Machinery",
            value: application?.beneficiary?.cost_machinery
              ? `₹${application.beneficiary?.cost_machinery}`
              : "-",
          },
          {
            label: "Invoice Date",
            value: application?.beneficiary?.invoice_date
              ? formatDate(application.beneficiary?.invoice_date)
              : "-",
          },
        ]
      : []),
    {
      label: "Premises Type",
      value: application?.beneficiary?.premises_type || "-",
    },
    {
      label: "Receiving Benefits under Other Schemes",
      value: application?.beneficiary?.has_other_schemes || "-",
    },
    ...(application?.beneficiary?.has_other_schemes === "yes"
      ? [
          {
            label: "Other State Government Scheme",
            value: application?.beneficiary?.other_state_gov_scheme || "-",
          },
          {
            label: "Scheme Details",
            value: application?.beneficiary?.other_scheme_details || "-",
          },
        ]
      : []),
  ];

  const spvDetails = [
    { label: "SPV Name", value: application?.spv_name },
    { label: "SPV Contact Number", value: application?.spv_contact_number },
    { label: "SPV Address", value: application?.spv_address },
    { label: "District", value: application?.district?.name },
    { label: "City", value: application?.spv_city },
    { label: "State", value: application?.spv_state },
    { label: "Pincode", value: application?.spv_pin_code },
    { label: "PAN", value: application?.spv_pan },
    { label: "TAN", value: application?.spv_tan },
    { label: "GST", value: application?.spv_gst },
    {
      label: "Registration details of SPV",
      value: application?.registration_details,
    },
    {
      label: "Date of formation of the company",
      value: application?.formation_date
        ? formatDate(application.formation_date)
        : "-",
    },
    {
      label: "Date of commencement of business",
      value: application?.business_commencement_date
        ? formatDate(application.business_commencement_date)
        : "-",
    },
    {
      label: "Bye laws or MoA and AoA submitted",
      value: application?.bye_laws,
    },
    { label: "Name of the Members in SPV", value: application?.spv_members },
    {
      label: "Main objectives of the SPV",
      value: application?.main_objectives,
    },
    {
      label: "Facilities to be Created",
      value: application?.main_facilities_proposed.join(", "),
    },
    {
      label: "Is the proposed CFC located in cluster",
      value: application?.cluster_details,
    },
  ];

  const priorityDetails = [
    {
      label:
        "Whether you have made any tie-up arrangement with manufacturer / running an own industry",
      value: application?.additional_detail?.is_tie_up_made,
    },
    {
      label: "Average Business Turnover (in Rs.)",
      value: application?.additional_detail?.turnover,
    },
    {
      label: "Does the SPV include new entrepreneurs?",
      value: application?.additional_detail?.new_entrepreneur,
    },
    ...(application?.additional_detail?.new_entrepreneur === "Yes"
      ? [
          {
            label: "Number of new entrepreneurs engaged in the SPV",
            value: application?.additional_detail?.new_spv,
          },
        ]
      : []),
    {
      label:
        "Willingness to Switch Over to Commercial Production and Innovations",
      value: application?.additional_detail?.willingness_to_switch,
    },
    ...(application?.additional_detail?.willingness_to_switch === "yes"
      ? [
          {
            label: "Number of new entrepreneurs engaged in the SPV",
            value: application?.additional_detail?.willingness_description,
          },
        ]
      : []),
    {
      label: "Details of Estimated Investment for the proposed CFC - Capital",
      value: application?.additional_detail?.capital_investment
        ? `₹${application?.additional_detail?.capital_investment}`
        : "₹0",
    },
    {
      label: "Details of Estimated Investment for the proposed CFC - Revenue",
      value: application?.additional_detail?.revenue
        ? `₹${application?.additional_detail?.revenue}`
        : "₹0",
    },
    {
      label: "Details of Estimated Investment for the proposed CFC - Total",
      value: application?.additional_detail?.total_investment
        ? `₹${application?.additional_detail?.total_investment}`
        : "₹0",
    },
    {
      label:
        "Details of Estimated Project Cost for the proposed CFC - Cost of Machinery",
      value: application?.additional_detail?.machinery_cost
        ? `₹${application?.additional_detail?.machinery_cost}`
        : "₹0",
    },
    {
      label:
        "Details of Estimated Project Cost for the proposed CFC - Cost of Infrastructure",
      value: application?.additional_detail?.infrastructure_cost
        ? `₹${application?.additional_detail?.infrastructure_cost}`
        : "₹0",
    },
    {
      label:
        "Details of Estimated Project Cost for the proposed CFC - Cost of Equipments",
      value: application?.additional_detail?.equipment_cost
        ? `₹${application?.additional_detail?.equipment_cost}`
        : "₹0",
    },
    {
      label:
        "Details of Estimated Project Cost for the proposed CFC - Cost of Shed",
      value: application?.additional_detail?.shed_cost
        ? `₹${application?.additional_detail?.shed_cost}`
        : "₹0",
    },
    {
      label: `Details of Estimated Project Cost for the proposed CFC - Any Other Cost (Specify) - ${
        application?.additional_detail?.other_project_cost_type ?? "Nil"
      }`,
      value: application?.additional_detail?.other_project_cost
        ? `₹${application?.additional_detail?.other_project_cost}`
        : "₹0",
    },
    {
      label: `Source of Income`,
      value: application?.additional_detail?.source_of_income.join(", "),
    },
    ...(application?.additional_detail?.source_of_income.includes("Own Fund")
      ? [
          {
            label: "Contribution of Own Fund (%)",
            value: application?.additional_detail?.own_fund_percent,
          },
        ]
      : []),
    ...(application?.additional_detail?.source_of_income.includes("Bank Loan")
      ? [
          {
            label: "Contribution of Bank Loan (%)",
            value: application?.additional_detail?.bank_loan_percent,
          },
        ]
      : []),
    ...(application?.additional_detail?.source_of_income.includes(
      "Private Finance"
    )
      ? [
          {
            label: "Contribution of Private Finance (%)",
            value: application?.additional_detail?.private_finance_percent,
          },
        ]
      : []),
    ...(application?.additional_detail?.source_of_income.includes(
      "Any other (specify)"
    )
      ? [
          {
            label: `Other Contribution (%) - ${application?.additional_detail?.other_percent_type}`,
            value: application?.additional_detail?.other_percent,
          },
        ]
      : []),
  ];

  const attachedFiles = [
    {
      label: "Uploaded PAN Document",
      value: application?.spv_pan_copy ?? undefined,
    },
    {
      label: "Uploaded TAN Document",
      value: application?.spv_tan_copy ?? undefined,
    },
    {
      label: "Uploaded GST Document",
      value: application?.spv_gst_copy ?? undefined,
    },
    {
      label: "Uploaded Registration Documents",
      value: application?.registration_copy ?? undefined,
    },
    {
      label: "Bye laws or MoA and AoA Documents",
      value: application?.bye_laws_copy ?? undefined,
    },
    {
      label: "Uploaded Members Aadhaar Document",
      value: application?.members_aadhaar_copy ?? undefined,
    },
    {
      label:
        "Undertaking certificate for not selling the Component for 5 years",
      value: application?.purchase_detail_bond ?? undefined,
    },
    {
      label: "Location of Common Facility Centre - GPS Location",
      value: application?.geo_tag ?? undefined,
    },
    {
      label: "If yes, Describe the details about the cluster",
      value: application?.about_cluster_copy ?? undefined,
    },
    {
      label: "Uploaded Rental Document",
      value: application?.beneficiary?.rental_copy ?? undefined,
    },
    {
      label: "Uploaded Lease Document",
      value: application?.beneficiary?.lease_copy ?? undefined,
    },
    {
      label: "Uploaded Electricity Bill Document",
      value: application?.beneficiary?.eb_bill_copy ?? undefined,
    },
    {
      label: "Uploaded Invoice Document",
      value: application?.beneficiary?.invoice_copy ?? undefined,
    },
    {
      label: "Uploaded Scheme Document",
      value: application?.beneficiary?.scheme_documents ?? undefined,
    },
    {
      label: "Uploaded Bank Passbook",
      value: application?.bank?.bank_passbook ?? undefined,
    },
    {
      label: "Uploaded Tie Up Arrangement Documents",
      value: application?.bank?.tie_up_attachment ?? undefined,
    },
  ];

  return (
    <div className="space-y-6">
      {!viewPage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <span>Application Overview</span>
            </CardTitle>
          </CardHeader>
          <CardContent style={{ marginTop: "15px" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p style={{ fontSize: "20px" }}>
                <strong>Application Number:</strong>{" "}
                {application?.application_no}
              </p>
              <p style={{ fontSize: "20px" }}>
                <strong>Completed On:</strong>{" "}
                {application?.is_completed === 1
                  ? `${formatDate(application?.updated_at)} ${new Date(
                      application?.updated_at
                    ).toLocaleTimeString("en-IN")}`
                  : "Still Not Completed"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <ReviewSection title="SPV Details" data={spvDetails} tabName="spv" />
      <ReviewSection
        title="Beneficiary Details"
        data={beneficiaryDetails}
        tabName="eligibility"
      />
      <ReviewSection
        title="Priority Details"
        data={priorityDetails}
        tabName="priority"
      />
      <FileReviewSection title="Uploaded Files" files={attachedFiles} />

      {viewPage && (
        <Card className="mb-4 bg-white p-3">
          <CardHeader className="p-4 padding-bottom-3 rounded-lg opacity-80 bg-teal-700 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Declaration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4 pt-4">
              {application?.declaration?.terms_accepted ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Accepted the declaration
                  </p>
                </>
              ) : (
                <>
                  <ShieldCloseIcon className="h-4 w-4 text-red-600 mr-2" />{" "}
                  <p className="font-semibold">
                    User Has Not Accepted the declaration
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="applicant_signature">
                Signature of the SPV Members{" "}
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                {application?.declaration?.applicant_signature ? (
                  <img
                    src={application?.declaration?.applicant_signature}
                    alt=""
                    className="mt-2 max-h-32"
                  />
                ) : (
                  <p>No Signature Attached</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApplicationPreview;
